import React, { useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography
} from '@mui/material';

// Icons
import HelpIcon from '@mui/icons-material/Help';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface ApplicationHelpPanelProps {
    initialShowHelp?: boolean;
}

const ApplicationHelpPanel: React.FC<ApplicationHelpPanelProps> = ({ initialShowHelp = false }) => {
    const [showHelp, setShowHelp] = useState<boolean>(initialShowHelp);

    return (
        <Accordion
            expanded={showHelp}
            onChange={() => setShowHelp(!showHelp)}
            sx={{
                mb: 3,
                borderRadius: 2,
                overflow: 'hidden',
                '&:before': {
                    display: 'none',
                },
                boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)'
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="help-content"
                id="help-header"
                sx={{
                    backgroundColor: 'background.paper',
                    borderBottom: showHelp ? '1px solid' : 'none',
                    borderColor: 'divider'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <InfoOutlinedIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        How to Create Your Application
                    </Typography>

                    {!showHelp && (
                        <Box sx={{ display: 'flex', ml: 'auto', alignItems: 'center', gap: 1 }}>
                            <Chip
                                size="small"
                                label="View Help Guide"
                                color="primary"
                                variant="outlined"
                                icon={<HelpIcon style={{ fontSize: '16px' }} />}
                            />
                        </Box>
                    )}
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ p: 3, pb: 2, backgroundColor: 'background.paper' }}>
                <Box>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        This section allows you to create and manage application forms for both mentors and mentees, with intelligent matching between their responses.
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Application Question Types
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Paper sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', bgcolor: 'background.paper' }}>
                            <Box component="span" sx={{ mr: 1, color: 'text.secondary' }}>👥 Both</Box>
                            <Typography variant="body2">Asked to both mentors & mentees</Typography>
                        </Paper>
                        <Paper sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', bgcolor: 'background.paper' }}>
                            <Box component="span" sx={{ mr: 1, color: 'text.secondary' }}>👨‍🏫 Mentor Only</Box>
                            <Typography variant="body2">Only shown to mentors</Typography>
                        </Paper>
                        <Paper sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', bgcolor: 'background.paper' }}>
                            <Box component="span" sx={{ mr: 1, color: 'text.secondary' }}>🧑‍🎓 Mentee Only</Box>
                            <Typography variant="body2">Only shown to mentees</Typography>
                        </Paper>
                    </Box>

                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Match Priority Levels
                    </Typography>

                    <List disablePadding>
                        <ListItem disablePadding sx={{ py: 0.75, px: 1, bgcolor: 'background.paper', mb: 0.5, borderRadius: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'error.main' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography variant="body2" sx={{ fontWeight: 'medium' }}>Critical Match Priority</Typography>}
                                secondary="Perfect match required (deal breaker)"
                            />
                        </ListItem>
                        <ListItem disablePadding sx={{ py: 0.75, px: 1, bgcolor: 'background.paper', mb: 0.5, borderRadius: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'warning.main' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography variant="body2" sx={{ fontWeight: 'medium' }}>High Match Priority</Typography>}
                                secondary="Strong preference for match"
                            />
                        </ListItem>
                        <ListItem disablePadding sx={{ py: 0.75, px: 1, bgcolor: 'background.paper', mb: 0.5, borderRadius: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'success.main' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography variant="body2" sx={{ fontWeight: 'medium' }}>Medium Match Priority</Typography>}
                                secondary="Considered in matching but not required"
                            />
                        </ListItem>
                        <ListItem disablePadding sx={{ py: 0.75, px: 1, bgcolor: 'background.paper', mb: 0.5, borderRadius: 1 }}>
                            <ListItemIcon sx={{ minWidth: 36, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                            </ListItemIcon>
                            <ListItemText
                                primary={<Typography variant="body2" sx={{ fontWeight: 'medium' }}>Low Match Priority</Typography>}
                                secondary="Minimal influence on matching"
                            />
                        </ListItem>
                    </List>

                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                        Key Features
                    </Typography>

                    <List disablePadding sx={{ ml: 2 }}>
                        <ListItem disablePadding sx={{ display: 'list-item', listStyleType: 'disc', mb: 0.5 }}>
                            <Typography variant="body2">
                                <strong>Role-specific questions:</strong> Create questions targeted for mentors, mentees, or both roles
                            </Typography>
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'list-item', listStyleType: 'disc', mb: 0.5 }}>
                            <Typography variant="body2">
                                <strong>Automatic matching:</strong> Role-specific questions automatically generate a matching question for the other role
                            </Typography>
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'list-item', listStyleType: 'disc', mb: 0.5 }}>
                            <Typography variant="body2">
                                <strong>Match importance:</strong> Set how critical each question is for the matching algorithm
                            </Typography>
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'list-item', listStyleType: 'disc', mb: 0.5 }}>
                            <Typography variant="body2">
                                <strong>AI question generation:</strong> Quickly create relevant questions with appropriate matching settings
                            </Typography>
                        </ListItem>
                        <ListItem disablePadding sx={{ display: 'list-item', listStyleType: 'disc', mb: 0.5 }}>
                            <Typography variant="body2">
                                <strong>Question organization:</strong> Group questions into logical sections
                            </Typography>
                        </ListItem>
                    </List>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default ApplicationHelpPanel;